/**
 * Reports app entries that came from a link we sent, in the browser.
 *
 * Entry on the web is an ordinary page load, so unlike native there is no handoff to
 * listen for and every visit looks alike. The filter below is what keeps the event
 * meaningful: a load counts as a followed link when it carries attribution we put on the
 * link, or when it arrived from somewhere that is not us. A bookmark or a typed address
 * is neither, and counting those would bury the campaigns this event exists to measure
 * under everyday traffic.
 *
 * Runs at startup, before client-side navigation can strip the query parameters — the
 * same reason `captureFirstTouchAttribution` runs where it does.
 */

import { trackDeepLinkOpened } from '@/util/analyticsEvents';
import { recordCommunicationAttribution } from '@/util/communicationAttribution';
import {
  describeLinkPath,
  hasReportedParams,
  readCommunicationAttribution,
} from '@/util/deepLinkAnalytics';
import { recordExpertLinkEntry } from '@/util/expertLinkAttribution';

/**
 * Whether a referrer came from this app rather than off-site.
 *
 * Compares parsed origins rather than matching a prefix: a host that merely starts with
 * ours — `app.primum.co.example.net` — passes a prefix test and would have its arrivals
 * silently dropped as internal navigation, which is the failure that shows up as a
 * campaign under-reporting for no visible reason.
 *
 * An unparseable referrer is treated as external, so a malformed one costs a
 * misclassified entry rather than a lost one.
 */
function isSameOrigin(referrer: string, origin: string): boolean {
  try {
    return new URL(referrer).origin === origin;
  } catch {
    return false;
  }
}

/**
 * Guards against a second report from a remount. Module-level rather than a ref, because
 * the entry being described is the page load itself, which happens once per module
 * instance no matter how the React tree is rebuilt.
 */
let reported = false;

export function initDeepLinkTracking(): () => void {
  if (typeof window === 'undefined' || reported) {
    return () => {};
  }

  reported = true;

  const { pathname, search, origin } = window.location;

  const { path, params } = describeLinkPath(`${pathname}${search}`);

  const attribution = readCommunicationAttribution(`${pathname}${search}`);

  // Must stay above the early return: an entry too ordinary to report as a followed link
  // still lands on a screen that should carry the tags.
  recordCommunicationAttribution(attribution);

  recordExpertLinkEntry(path, params.via);

  const referrer = typeof document === 'undefined' ? '' : document.referrer;

  const isExternalReferrer = Boolean(referrer) && !isSameOrigin(referrer, origin);

  const hasAttribution = hasReportedParams(params) || Boolean(attribution.communicationId);

  if (!hasAttribution && !isExternalReferrer) {
    return () => {};
  }

  trackDeepLinkOpened({
    path,
    linkType: 'web',
    // Every browser entry is a fresh document, so there is no warm case to distinguish.
    launchType: 'cold',
    params,
    ...attribution,
  });

  return () => {};
}

/** Test seam, mirroring the native module so callers stay platform-agnostic. */
export function resetDeepLinkTracking(): void {
  reported = false;
}
